import React from 'react';
import { Activity, Fingerprint, Layers, Sparkles } from 'lucide-react';

const CHIPS = [
  { icon: Layers, label: 'Design system', pos: 'left-0 top-[16%]', delay: '0.7s' },
  { icon: Activity, label: '60 fps', pos: 'right-0 top-[34%]', delay: '0.9s' },
  { icon: Fingerprint, label: 'Secure by default', pos: 'left-[4%] bottom-[18%]', delay: '1.05s' },
  { icon: Sparkles, label: 'Shipped', pos: 'right-[6%] bottom-[10%]', delay: '1.2s' },
];

/**
 * Abstract hero composition — an app surface under construction, drawn entirely
 * in CSS/SVG so there is no stock imagery and nothing extra to download.
 */
const HeroVisual: React.FC = () => (
  <div className="relative mx-auto aspect-square w-full max-w-[520px]">
    {/* Concentric orbit rings */}
    <div className="absolute inset-0 animate-spinslow">
      <div className="absolute inset-[6%] rounded-full border border-white/[0.07]" />
      <div className="absolute inset-[20%] rounded-full border border-dashed border-peri-400/20" />
      <div className="absolute inset-[34%] rounded-full border border-white/[0.05]" />
      <span className="absolute left-1/2 top-[6%] h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-peri-300 shadow-[0_0_14px_3px_rgba(139,154,240,0.55)]" />
      <span className="absolute bottom-[20%] left-[20%] h-1 w-1 rounded-full bg-orchid-400" />
    </div>

    {/* Device. Positioning sits on the wrapper so the entrance keyframes own the
        element's own transform without fighting the centring utilities. */}
    <div className="absolute left-1/2 top-1/2 h-[74%] w-[38%] -translate-x-1/2 -translate-y-1/2">
      <div className="enter-device h-full w-full rounded-[30px] border border-white/12 bg-gradient-to-b from-ink-800 to-ink-900 p-2.5 shadow-lift">
        <div className="flex h-full w-full flex-col gap-3 overflow-hidden rounded-[22px] border border-white/[0.07] bg-ink-950 p-4">
          <div className="mx-auto h-1 w-8 rounded-full bg-white/15" />
          <div className="mt-2 h-16 rounded-xl bg-brand-sweep opacity-90" />
          <div className="space-y-2">
            <div className="h-2 w-3/4 rounded-full bg-white/12" />
            <div className="h-2 w-1/2 rounded-full bg-white/[0.07]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-9 rounded-lg border border-white/[0.07] bg-white/[0.025]" />
            ))}
          </div>
          <div className="mt-auto h-8 rounded-full bg-peri-400/25" />
        </div>
      </div>
    </div>

    {/* Floating chips */}
    {CHIPS.map(({ icon: Icon, label, pos, delay }) => (
      <div
        key={label}
        style={{ animationDelay: delay }}
        className={`surface enter-chip absolute ${pos} flex items-center gap-2 rounded-full px-3.5 py-2`}
      >
        <Icon size={13} strokeWidth={1.7} className="text-peri-300" />
        <span className="font-mono text-[9.5px] uppercase tracking-label text-paper/70">
          {label}
        </span>
      </div>
    ))}
  </div>
);

export default HeroVisual;
